import React from 'react';
import { ShieldCheck, Lock, Loader2, Tag } from 'lucide-react';

const money = (n) => `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;

export const quoteFor = (req, picked, pricing) => {
  const tiers = [0, Number(pricing?.discount_2 ?? 10), Number(pricing?.discount_3 ?? 15)];
  const base = Number(req.price) || 0;
  const offset = (req.purchased_versions || []).length;
  const lines = picked.map((id, i) => {
    const pct = tiers[Math.min(offset + i, 2)];
    return { id, pct, price: Math.round(base * (1 - pct / 100) * 100) / 100, title: req.versions.find((v) => v.id === id)?.title };
  });
  return { lines, total: Math.round(lines.reduce((s, l) => s + l.price, 0) * 100) / 100 };
};

export function MultiBuyPromo({ req, pricing }) {
  const owned = req.purchased_versions || [];
  const remaining = (req.versions || []).filter((v) => !owned.includes(v.id));
  if (remaining.length < 2) return null;
  const d2 = Number(pricing?.discount_2 ?? 10);
  const d3 = Number(pricing?.discount_3 ?? 15);
  return (
    <div className="rounded-2xl border border-gold/25 bg-gold/[0.04] p-5 mt-6" data-testid="multi-buy-promo">
      <p className="text-base md:text-lg text-cream font-semibold leading-snug">Listen to the songs below and select the song you want to purchase.</p>
      <p className="flex items-center gap-2 text-sm text-cream font-semibold mt-3"><Tag size={14} className="text-gold" /> Save when you purchase multiple songs.</p>
      <ul className="text-sm text-cream/75 mt-1.5 leading-relaxed space-y-1" data-testid="multi-buy-tiers">
        <li>* Purchase 2 songs &mdash; get <span className="text-gold">{d2}% off</span> your second song.</li>
        <li>* Purchase all 3 songs &mdash; get <span className="text-gold">{d3}% off</span> your third song.</li>
      </ul>
      {owned.length > 0 && <p className="text-sm text-cream/55 mt-1.5">The {owned.length === 1 ? 'version you already own counts' : `${owned.length} versions you already own count`} toward your discount.</p>}
      <p className="text-sm font-bold text-gold mt-3" data-testid="discount-disclaimer">Discounts apply only to each additional song, not to the entire purchase.</p>
    </div>
  );
}

export default function PurchaseBar({ req, picked, pricing, busy, onBuy }) {
  const owned = req.purchased_versions || [];
  const remaining = req.versions.filter((v) => !owned.includes(v.id));
  if (!remaining.length) return null;
  const { lines, total } = quoteFor(req, picked, pricing);
  const next = quoteFor(req, [remaining[0].id], pricing).lines[0];
  const n = picked.length;
  const showLines = n > 1 || (n === 1 && lines[0].pct > 0);
  return (
    <div className="mt-7 pt-6 border-t hairline" data-testid="purchase-bar">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          {showLines && (
            <ul className="mb-3 space-y-1" data-testid="price-breakdown">
              {lines.map((l) => (
                <li key={l.id} className="text-sm text-cream/75" data-testid={`price-line-${l.id}`}>
                  Version {l.id} <span className={l.pct ? 'line-through text-cream/40 mx-1' : 'text-cream ml-1'}>{money(req.price)}</span>
                  {l.pct > 0 && <><span className="text-cream">{money(l.price)}</span> <span className="text-gold">Discounted Price ({l.pct}% off Reg Price)</span></>}
                </li>
              ))}
            </ul>
          )}
          {!n && next.pct > 0 && <p className="mb-3 text-sm text-cream/75" data-testid="next-discount-note"><span className="line-through text-cream/40 mr-1">{money(req.price)}</span><span className="text-gold">{next.pct}% off Reg Price</span> on your next version</p>}
          <p className="font-display text-4xl text-cream" data-testid="total-price"><span className="text-cream/60 text-2xl mr-2">Total:</span>{money(n ? total : next.price)}</p>
          <p className="eyebrow text-[9px] mt-1">{n > 1 ? `${n} songs` : 'One-time'} &middot; downloads + license &middot; before tax</p>
          <p className="text-[11px] text-cream/40 mt-1" data-testid="tax-note">Sales tax, where applicable, is calculated by Stripe at checkout based on your billing address.</p>
        </div>
        <button data-testid="buy-song-btn" onClick={onBuy} disabled={!n || busy} className={`rounded-full px-7 py-3.5 flex items-center gap-2 transition-all ${!n ? 'btn-ghost opacity-60 cursor-not-allowed' : 'btn-gold'}`}>
          {busy ? <Loader2 size={16} className="animate-spin" /> : !n ? <><Lock size={16} /> Choose a version first</> : <><ShieldCheck size={16} /> Buy {n > 1 ? `${n} versions` : `version ${picked[0]}`} &middot; {money(total)}</>}
        </button>
      </div>
    </div>
  );
}
